import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCleaners, addCleaner, getAssignments } from '../services/api';
import LoadingScreen from '../components/LoadingScreen';

const CleanersPage = () => {
  const [cleaners, setCleaners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCleaner, setNewCleaner] = useState({ name: '', phone: '', preferred_language: 'es' });
  const [saving, setSaving] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cleanersData, assignmentsRes] = await Promise.all([
        getCleaners(),
        getAssignments(),
      ]);
      setCleaners(cleanersData || []);
      setAssignments(assignmentsRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPropertyCount = (cleanerId) => {
    return assignments.filter(a => a.cleaner_id === cleanerId).length;
  };

  const handleAddCleaner = async () => {
    if (!newCleaner.name || !newCleaner.phone) return;
    setSaving(true);
    try {
      const result = await addCleaner(newCleaner);
      setShowAddModal(false);
      setNewCleaner({ name: '', phone: '', preferred_language: 'es' });
      if (result?.id) {
        navigate(`/cleaners/${result.id}/manage`);
      } else {
        fetchData();
      }
    } catch (err) {
      console.error('Error adding cleaner:', err);
      if (err.response && err.response.data?.error) {
        alert(err.response.data.error);
      } else {
        alert('Failed to create cleaner.');
      }
    } finally {
      setSaving(false);
    }
  };

  const filteredCleaners = cleaners.filter(c =>
    (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.phone || '').includes(searchTerm)
  );

  if (loading) return <LoadingScreen message="Loading aynistas..." />;

  return (
    <div className="ayni-page">
      {/* Header */}
      <div className="ayni-head">
        <h1 className="ayni-display-lg" style={{ margin: 0 }}>Aynistas</h1>
        <p className="ayni-statline" style={{ marginTop: 8 }}>
          <strong>{cleaners.length}</strong>{cleaners.length === 1 ? 'aynista' : 'aynistas'}
        </p>
      </div>

      {/* Toolbar */}
      <div className="ayni-toolbar">
        <div className="ayni-toolbar-group" />
        <div className="ayni-toolbar-group">
          <input
            className="ayni-search"
            type="text"
            placeholder="Search aynistas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="ayni-btn primary" onClick={() => setShowAddModal(true)}>
            + Add Aynista
          </button>
        </div>
      </div>

      {/* Cleaners list */}
      {filteredCleaners.length === 0 ? (
        <div className="ayni-empty">No aynistas found. Click "+ Add Aynista" to create one.</div>
      ) : (
        <div className="ayni-card">
          <table className="ayni-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Language</th>
                <th>Properties</th>
                <th aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {filteredCleaners.map((cleaner) => (
                <tr key={cleaner.id} onClick={() => navigate(`/cleaners/${cleaner.id}/manage`)}>
                  <td><div className="ayni-rowname">{cleaner.name}</div></td>
                  <td className="ayni-meta">{cleaner.phone || '—'}</td>
                  <td>
                    <span className={`ayni-badge ${cleaner.preferred_language === 'es' ? 'amber' : 'blue'}`}>
                      {cleaner.preferred_language === 'es' ? 'ES' : 'EN'}
                    </span>
                  </td>
                  <td className="ayni-meta">{getPropertyCount(cleaner.id)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>
                    <i className="fa-solid fa-chevron-right" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Cleaner Modal */}
      {showAddModal && (
        <div className="ayni-modal" onClick={() => setShowAddModal(false)}>
          <div className="ayni-modal-panel" onClick={(e) => e.stopPropagation()}>
            <h2 className="ayni-modal-title">Add New Aynista</h2>

            <div className="ayni-field">
              <label className="ayni-label">Name *</label>
              <input
                className="ayni-input"
                value={newCleaner.name}
                onChange={(e) => setNewCleaner({ ...newCleaner, name: e.target.value })}
                placeholder="Cleaner name"
              />
            </div>

            <div className="ayni-field">
              <label className="ayni-label">Phone *</label>
              <input
                className="ayni-input"
                value={newCleaner.phone}
                onChange={(e) => setNewCleaner({ ...newCleaner, phone: e.target.value })}
                placeholder="Phone number"
              />
            </div>

            <div className="ayni-field">
              <label className="ayni-label">Language</label>
              <select
                className="ayni-select"
                value={newCleaner.preferred_language}
                onChange={(e) => setNewCleaner({ ...newCleaner, preferred_language: e.target.value })}
              >
                <option value="es">Español</option>
                <option value="en">English</option>
              </select>
            </div>

            <div className="ayni-modal-actions">
              <button
                className="ayni-btn primary"
                onClick={handleAddCleaner}
                disabled={saving || !newCleaner.name || !newCleaner.phone}
              >
                {saving ? 'Creating...' : 'Create'}
              </button>
              <button
                className="ayni-btn ghost"
                onClick={() => { setShowAddModal(false); setNewCleaner({ name: '', phone: '', preferred_language: 'es' }); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CleanersPage;
